import type {
    AnyObject,
    BlockAbstract,
    ContainerBlockAbstract,
    ContainerViewChildren,
    FormContainerBlockAbstract,
    PageItem,
    ViewOptions
} from '@lighthouse/core'
import { BlockType } from '@lighthouse/core'

import type { FlowLayoutNode } from '../components'
import { LOGIN_PAGE } from '../constants'
import {
    findBlockById,
    getBlockChildren,
    getWithScopeId,
    isButtonBlock,
    isContainerBlock,
    isContainerPanel,
    isCustomViewBlock,
    isFloatBoxBlock,
    isFormContainerBlock,
    isIconBlock,
    isTextBlock
} from './block'

const filterNilLayoutNode = (node: FlowLayoutNode | null): node is FlowLayoutNode => {
    return !!node
}

export type PageBlockRes = (Omit<BlockAbstract, 'config'> & {
    config: Record<BlockType, AnyObject>
})[]

/** 将后端block数据转换为前端需要的格式 */
export function transformBlockRes2Blocks(blockRes: PageBlockRes): BlockAbstract[] {
    try {
        return blockRes.reduce<BlockAbstract[]>((total, curr) => {
            const { config, type } = curr
            if (type === BlockType.container) {
                return [
                    ...total,
                    {
                        ...curr,
                        config: config[type],
                        children: (curr as unknown as ContainerBlockAbstract).children.map(view => {
                            return {
                                ...view,
                                children: transformBlockRes2Blocks(view.children as unknown as PageBlockRes)
                            }
                        })
                    } as BlockAbstract
                ]
            }

            if (
                type === BlockType.formContainer ||
                (type === BlockType.view && (config.view as ViewOptions).viewType === 'custom') ||
                type === BlockType.floatBox ||
                type === BlockType.text ||
                type === BlockType.icon ||
                type === BlockType.button
            ) {
                return [
                    ...total,
                    {
                        ...curr,
                        config: config[type],
                        children: transformBlockRes2Blocks(
                            (curr as unknown as FormContainerBlockAbstract).children as unknown as PageBlockRes
                        )
                    } as BlockAbstract
                ]
            }

            return [...total, { ...curr, config: config[type] } as BlockAbstract]
        }, [])
    } catch (error) {
        console.log('🚀 ~ transformBlockRes2Blocks ~ error:', error)
        return []
    }
}

/** 将后端同步组件数据转换为前端需要的格式 */
export function transformSyncComponentsRes2Blocks(res: PageBlockRes, isLeaf?: boolean): BlockAbstract[] {
    try {
        return res.reduce<BlockAbstract[]>((total, curr) => {
            const { config, type } = curr
            if (type === BlockType.container) {
                return [
                    ...total,
                    {
                        ...curr,
                        config: config[type],
                        children: (curr as unknown as ContainerBlockAbstract).children.map(view => {
                            return {
                                ...view,
                                children: transformSyncComponentsRes2Blocks(view.children as unknown as PageBlockRes, true)
                            }
                        }),
                        isLeafSynchronous: isLeaf,
                        isMasterSynchronous: !isLeaf
                    } as ContainerBlockAbstract
                ]
            }

            if (
                type === BlockType.formContainer ||
                (type === BlockType.view && (config.view as ViewOptions).viewType === 'custom') ||
                type === BlockType.floatBox ||
                type === BlockType.button ||
                type === BlockType.icon ||
                type === BlockType.text
            ) {
                return [
                    ...total,
                    {
                        ...curr,
                        config: config[type],
                        children: transformSyncComponentsRes2Blocks(
                            (curr as unknown as FormContainerBlockAbstract).children as unknown as PageBlockRes,
                            true
                        ),
                        isLeafSynchronous: isLeaf,
                        isMasterSynchronous: !isLeaf
                    } as BlockAbstract
                ]
            }
            return [...total, { ...curr, config: config[type], isLeafSynchronous: isLeaf, isMasterSynchronous: !isLeaf } as BlockAbstract]
        }, [])
    } catch (error) {
        return []
    }
}

type TransformParams = {
    blocks: BlockAbstract[]
    syncComponents: ContainerBlockAbstract[]
}

/** 将blocks转换为栅格节点格式 */
export function transformBlock2FlowLayoutNode({ blocks, syncComponents }: TransformParams): FlowLayoutNode[] {
    function recursion(tree: BlockAbstract[] | ContainerViewChildren[], parentIsCustomView?: boolean, scope?: string): FlowLayoutNode[] {
        return tree
            .map<FlowLayoutNode | null>(n => {
                if (isContainerPanel(n)) {
                    return {
                        id: n.id,
                        scope,
                        type: 'panel',
                        children: recursion(n.children ?? [], parentIsCustomView, scope)
                    }
                }
                const isMasterSyncBlock = !!n.synchronousId

                let block: BlockAbstract | undefined

                if (scope) {
                    block = findBlockById(n.id, syncComponents)
                } else if (isMasterSyncBlock) {
                    block = fillSyncBlock(n, syncComponents)
                } else {
                    block = n
                }

                if (!block) {
                    return null
                }

                if (block.type === BlockType.view && block.config.viewType === 'custom') {
                    return {
                        id: block.id,
                        scope,
                        isRootSyncNode: isMasterSyncBlock,
                        type: 'custom',
                        block,
                        data: { ...block.config.breakPoint, cursor: block.config.cursor },
                        children: recursion(block.children ?? [], true, isMasterSyncBlock ? n.id : scope)
                    }
                }

                if (isContainerBlock(block)) {
                    const { id, config, children } = block

                    return {
                        id,
                        scope,
                        isRootSyncNode: isMasterSyncBlock,
                        type: 'container',
                        block,
                        data: { ...config.breakPoint, cursor: config.cursor },
                        children: recursion(children ?? [], parentIsCustomView, isMasterSyncBlock ? n.id : scope)
                    }
                }

                if (isFormContainerBlock(block)) {
                    return {
                        id: block.id,
                        scope,
                        isRootSyncNode: isMasterSyncBlock,
                        type: 'formContainer',
                        block,
                        data: { ...block.config.breakPoint, cursor: block.config.cursor },
                        children: recursion(block.children, parentIsCustomView, isMasterSyncBlock ? n.id : scope)
                    }
                }

                if (isFloatBoxBlock(block)) {
                    return {
                        id: n.id,
                        scope,
                        isRootSyncNode: isMasterSyncBlock,
                        type: 'overlay',
                        disableDrag: true,
                        block,
                        data: { ...block.config.breakPoint, cursor: block.config.cursor },
                        children: recursion(block.children, parentIsCustomView, isMasterSyncBlock ? n.id : scope)
                    }
                }

                if (isTextBlock(block) || isButtonBlock(block) || isIconBlock(block)) {
                    return {
                        id: n.id,
                        scope,
                        isRootSyncNode: isMasterSyncBlock,
                        type: 'block',
                        disableDrag: true,
                        block,
                        data: { ...block.config.breakPoint, cursor: block.config.cursor },
                        children: recursion(block.children, parentIsCustomView, isMasterSyncBlock ? n.id : scope)
                    }
                }

                return {
                    id: block.id,
                    scope,
                    isRootSyncNode: isMasterSyncBlock,
                    block,
                    data: { ...block.config.breakPoint, cursor: block.config.cursor },
                    type: 'block'
                }
            })
            .filter(filterNilLayoutNode)
    }
    return recursion(blocks)
}

/** 将同步组件数据同步到同步组件实例中 */
export const fillSyncBlock = (block: BlockAbstract, syncComponents: BlockAbstract[]): BlockAbstract => {
    if (block.synchronousId) {
        return {
            ...block,
            ...findBlockById(block.synchronousId, syncComponents),
            id: block.id
        }
    }

    return block
}

export const deepFillSyncBlock = (blocks: BlockAbstract[], syncComponents: BlockAbstract[]): BlockAbstract[] => {
    return blocks.reduce<BlockAbstract[]>((total, curr) => {
        if (isContainerBlock(curr)) {
            if (curr.synchronousId) {
                return [...total, fillSyncBlock(curr, syncComponents)]
            }

            return [
                ...total,
                {
                    ...curr,
                    children: curr.children.map(view => {
                        return {
                            ...view,
                            children: deepFillSyncBlock(view.children, syncComponents)
                        }
                    })
                }
            ]
        }

        if (isFormContainerBlock(curr) || isCustomViewBlock(curr)) {
            if (curr.synchronousId) {
                return [...total, fillSyncBlock(curr, syncComponents)]
            }

            return [
                ...total,
                {
                    ...curr,
                    children: deepFillSyncBlock(curr.children, syncComponents)
                }
            ]
        }

        return [...total, fillSyncBlock(curr, syncComponents)]
    }, [])
}

/** 平铺页面树 */
export function floatPageTree(items: PageItem[], list: PageItem[], path?: string): PageItem[] {
    for (const item of items) {
        item.type === 'COMMON' && list.push({ ...item, path })
        if (item.type === 'GROUP') {
            list = [...list, { ...item, children: [] }, ...floatPageTree(item.children, [], path ? `${path}/${item.name}` : item.name)]
        }
    }
    return list
}

export const getSystemPageUrl = (v: string) => {
    return v === LOGIN_PAGE ? `/system/account/login` : v
}
